/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext, useRef } from 'react'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import PropTypes from 'prop-types'
import { Auth } from 'aws-amplify'
import { PasswordInput } from '../PasswordInput/PasswordInput'
import { PasswordCheck } from '../PasswordCheck/PasswordCheck'
import { containsEmail } from '../../../utils/functions'
import { useTranslation } from 'react-i18next'
import Checkbox from '@mui/material/Checkbox'
import { NotificationContext } from '../../../context/Shared/Notification.context'
import { theme } from '../../../styles/theme.style'
import { BrandedButton } from '../../Shared/BrandedButton/BrandedButton.component'

import './SignInForm.style.scss'

export const SignInForm = ({ prefilledEmail, prefilledPassword = '', autoLogin = false }) => {
  const { t, i18n } = useTranslation()
  const [email, setEmail] = useState(prefilledEmail)
  const [password, setPassword] = useState(prefilledPassword)
  const autoLoginFired = useRef(false)
  const [newPassword, setNewPassword] = useState('')
  const [userErrorText, setUserErrorText] = useState('')
  const [passwordErrorText, setPasswordErrorText] = useState('')
  const [newPasswordErrorText, setNewPasswordErrorText] = useState('')
  const [user, setUser] = useState(null)
  const [showDefault, setShowDefault] = useState(true)
  const [showPasswordReset, setShowPasswordReset] = useState(false)
  const [showToken, setShowToken] = useState(false)
  const [code, setCode] = useState('')
  const [challengeName, setChallengeName] = useState(null)
  const [isAccepted, setIsAccepted] = useState(false)
  const { setSnackBarMessage } = useContext(NotificationContext)

  const signIn = async () => {
    try {
      const cUser = await Auth.signIn(email.trim(), password.trim())
      if (cUser.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setUser(cUser)
        setShowDefault(false)
        setShowPasswordReset(true)
        setShowToken(false)
      }
      if (
        cUser.challengeName === 'SMS_MFA' ||
        cUser.challengeName === 'SOFTWARE_TOKEN_MFA'
      ) {
        setUser(cUser)
        setShowDefault(false)
        setShowPasswordReset(false)
        setChallengeName(cUser.challengeName)
        setShowToken(true)
      }
    } catch (err) {
      setSnackBarMessage(err.message, 'error')
    }
  }

  const completeNewPassword = async () => {
    try {
      const cUser = await Auth.completeNewPassword(user, newPassword)
      if (
        cUser.challengeName === 'SMS_MFA' ||
        cUser.challengeName === 'SOFTWARE_TOKEN_MFA'
      ) {
        setUser(cUser)
        setShowDefault(false)
        setShowPasswordReset(false)
        setChallengeName(cUser.challengeName)
        setShowToken(true)
      }
    } catch (err) {
      setSnackBarMessage(err.message, 'error')
    }
  }

  const confirmSignIn = async () => {
    try {
      const cUser = await Auth.confirmSignIn(user, code, challengeName)
    } catch (err) {
      setSnackBarMessage(err.message, 'error')
    }
  }

  useEffect(() => {
    if (autoLogin && email.length > 0 && password.length > 0 && !autoLoginFired.current) {
      autoLoginFired.current = true
      signIn()
    }
  }, [])

  useEffect(() => {
    if (email.length > 0) {
      if (!containsEmail(email)) {
        setUserErrorText('Please enter a valid email address')
      } else {
        setUserErrorText('')
      }
    }
  }, [email])

  return (
    <div>
      {showDefault && (
        <div className="loginForm">
          <TextField
            id="outlined-name"
            label={t('Email')}
            className="textInput"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={userErrorText.length > 0}
            helperText={userErrorText}
            fullWidth
            sx={{ 
              marginBottom: 2,
              width: '100%',
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'transparent',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.25)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.25)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.25)',
                },
                '& input': {
                  color: 'white !important',
                  '&:-webkit-autofill': {
                    WebkitTextFillColor: 'white !important',
                    WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                    boxShadow: '0 0 0 1000px transparent inset !important',
                    transition: 'background-color 5000s ease-in-out 0s',
                  },
                  '&:-webkit-autofill:hover': {
                    WebkitTextFillColor: 'white !important',
                    WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                    boxShadow: '0 0 0 1000px transparent inset !important',
                  },
                  '&:-webkit-autofill:focus': {
                    WebkitTextFillColor: 'white !important',
                    WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                    boxShadow: '0 0 0 1000px transparent inset !important',
                  },
                },
                color: 'white',
              },
              '& .MuiInputLabel-root': {
                color: 'white',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'white',
              },
              '& .MuiFormHelperText-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
            }}
          />
          <PasswordInput
            label={t('Password')}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            onKeyPress={(event) => {
              if (event.key === 'Enter') {
                signIn()
              }
            }}
            error={passwordErrorText.length > 0}
            helperText={passwordErrorText}
            fullWidth
            sx={{ 
              marginBottom: 2,
              width: '100%',
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'transparent',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.25)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.25)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.25)',
                },
                '& input': {
                  color: 'white !important',
                  '&:-webkit-autofill': {
                    WebkitTextFillColor: 'white !important',
                    WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                    boxShadow: '0 0 0 1000px transparent inset !important',
                    transition: 'background-color 5000s ease-in-out 0s',
                  },
                  '&:-webkit-autofill:hover': {
                    WebkitTextFillColor: 'white !important',
                    WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                    boxShadow: '0 0 0 1000px transparent inset !important',
                  },
                  '&:-webkit-autofill:focus': {
                    WebkitTextFillColor: 'white !important',
                    WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                    boxShadow: '0 0 0 1000px transparent inset !important',
                  },
                },
                color: 'white',
              },
              '& .MuiInputLabel-root': {
                color: 'white',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'white',
              },
              '& .MuiFormHelperText-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
            }}
          />
          <BrandedButton
            onClick={signIn}
            disabled={
              email.length === 0 ||
              password.length === 0 ||
              userErrorText.length > 0
            }
            buttonLabel={'Login'}
          />
        </div>
      )}
      {showPasswordReset && (
        <div className="loginForm">
          <p style={{ color: 'white', marginBottom: '16px' }}>{t('Select a new password')}</p>
          <PasswordInput
            label={t('New Password')}
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            error={newPasswordErrorText.length > 0}
            helperText={newPasswordErrorText}
            fullWidth
            sx={{ 
              marginBottom: 2,
              width: '100%',
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'transparent',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.25)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.25)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.25)',
                },
                '& input': {
                  color: 'white !important',
                  '&:-webkit-autofill': {
                    WebkitTextFillColor: 'white !important',
                    WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                    boxShadow: '0 0 0 1000px transparent inset !important',
                    transition: 'background-color 5000s ease-in-out 0s',
                  },
                  '&:-webkit-autofill:hover': {
                    WebkitTextFillColor: 'white !important',
                    WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                    boxShadow: '0 0 0 1000px transparent inset !important',
                  },
                  '&:-webkit-autofill:focus': {
                    WebkitTextFillColor: 'white !important',
                    WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                    boxShadow: '0 0 0 1000px transparent inset !important',
                  },
                },
                color: 'white',
              },
              '& .MuiInputLabel-root': {
                color: 'white',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'white',
              },
              '& .MuiFormHelperText-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
            }}
          />
          <PasswordCheck password={newPassword} />
          <div className="loginForm__checkbox">
            <Checkbox
              checked={isAccepted}
              onChange={(event) => setIsAccepted(event.target.checked)}
            />
            <p>
              I agree to the &nbsp;
              <a href={theme.TERMS_OF_SERVICE} target="_blank" rel="noreferrer">
                Terms of Use
              </a>
              &nbsp; and &nbsp;
              <a href={theme.PRIVACY_POLICY} target="_blank" rel="noreferrer">
                Privacy Policy
              </a>
            </p>
          </div>
          <BrandedButton
            onClick={completeNewPassword}
            disabled={
              newPassword.length === 0 ||
              newPasswordErrorText.length > 0 ||
              !isAccepted
            }
            buttonLabel={'Change Password'}
          />
        </div>
      )}
      {showToken && (
        <div className="loginForm">
          <p style={{ color: 'white', marginBottom: '16px' }}>
            {t('Please enter your verification code')}{' '}
            {challengeName === 'SOFTWARE_TOKEN_MFA'
              ? t('(Authenticator App Based)')
              : t('(SMS Based)')}
          </p>
          <TextField
            id="outlined-name"
            label={t('Verification Code')}
            className="textInput"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            fullWidth
            sx={{ 
              marginBottom: 2,
              width: '100%',
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'transparent',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.25)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.25)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.25)',
                },
                '& input': {
                  color: 'white !important',
                  '&:-webkit-autofill': {
                    WebkitTextFillColor: 'white !important',
                    WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                    boxShadow: '0 0 0 1000px transparent inset !important',
                    transition: 'background-color 5000s ease-in-out 0s',
                  },
                  '&:-webkit-autofill:hover': {
                    WebkitTextFillColor: 'white !important',
                    WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                    boxShadow: '0 0 0 1000px transparent inset !important',
                  },
                  '&:-webkit-autofill:focus': {
                    WebkitTextFillColor: 'white !important',
                    WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                    boxShadow: '0 0 0 1000px transparent inset !important',
                  },
                },
                color: 'white',
              },
              '& .MuiInputLabel-root': {
                color: 'white',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'white',
              },
              '& .MuiFormHelperText-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
            }}
          />
          <BrandedButton
            onClick={confirmSignIn}
            disabled={code.length === 0}
            buttonLabel={'Confirm'}
          />
        </div>
      )}
    </div>
  )
}

SignInForm.propTypes = {
  prefilledEmail: PropTypes.string,
  prefilledPassword: PropTypes.string,
  autoLogin: PropTypes.bool
}
